import { format, isValid } from 'date-fns';

import { dateTimeWordedPattern, toDateString } from './format';
import { DATE_FORMATS, type DateInput } from './formats';
import { appLanguage, dateLocale } from './locales';
import { parseDate, toDate } from './parse';
import { ZonedDate } from './zonedDate';
import { wallTimeInZone } from './zonedWallTime';

/** The browser's IANA time zone, e.g. "Europe/Oslo". */
export function guessTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** The instant read in `timeZone`, or null when either is not valid. */
function inZone(timestamp: DateInput, timeZone: string): ZonedDate | null {
    const d = toDate(timestamp);
    if (!d) return null;
    const zoned = new ZonedDate(d.getTime(), timeZone);
    return isValid(zoned) ? zoned : null;
}

/**
 * The calendar day (`yyyy-MM-dd`) a timestamp falls on in `timezone`, for grouping by the
 * location's day rather than UTC's or the browser's.
 *
 * @example
 * extractDayInTimezone('2024-01-16T23:30:00.000Z', 'Europe/Oslo'); // "2024-01-17"
 * extractDayInTimezone('2024-01-17T02:00:00.000Z', 'America/New_York'); // "2024-01-16"
 */
export function extractDayInTimezone(
    timestamp: string | null | undefined,
    timezone = 'UTC'
): string {
    if (!timestamp) return '';
    const zoned = inZone(timestamp, timezone);
    return zoned ? format(zoned, DATE_FORMATS.internal) : '';
}

/**
 * The UTC instant of midnight in `timezone` on a picked calendar day, so from/to query
 * params follow the location's days whatever the browser's zone.
 *
 * @param date - The picked day: a local-midnight Date (as date pickers give) or a plain
 *   `yyyy-MM-dd` string. A timestamp string is refused with "": which day it falls on
 *   depends on a zone this function cannot guess. Extract the day first with
 *   `extractDayInTimezone(ts, tz)`.
 *
 * @example
 * dayStartISOInTimezone('2026-06-10', 'Europe/Oslo'); // "2026-06-09T22:00:00.000Z"
 * dayStartISOInTimezone('2026-06-10', 'America/Santiago'); // "2026-06-10T04:00:00.000Z"
 * dayStartISOInTimezone('2026-06-09T22:00:00.000Z', 'Europe/Oslo'); // ""
 */
export function dayStartISOInTimezone(date: DateInput, timezone = 'UTC'): string {
    if (typeof date === 'string' && date.includes('T')) return '';

    const dayStr = toDateString(date);
    if (!dayStr) return '';

    const midnight = wallTimeInZone(dayStr, timezone);
    return isValid(midnight) ? new Date(midnight.getTime()).toISOString() : '';
}

/**
 * A date-only API value, which the server sends as UTC midnight (`2999-01-01T00:00:00.000Z`),
 * as that calendar day at local midnight, for a date picker or a comparison with local days.
 * Reading the instant as it is lands on the day before anywhere west of UTC. The way back is
 * `dayStartISOInTimezone(day)`, which sends the picked day as UTC midnight.
 *
 * @example
 * storedDayAsLocalDate('2999-01-01T00:00:00.000Z'); // 1 January 2999, local midnight
 * storedDayAsLocalDate('2999-01-01'); // the same day
 */
export function storedDayAsLocalDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    // A bare `yyyy-MM-dd` is the day already; reading it as an instant first would put it
    // at local midnight, which is the day before in UTC east of Greenwich.
    return parseDate(value.includes('T') ? extractDayInTimezone(value) : value);
}

/**
 * The calendar day of a stored licence boundary in UTC (`yyyy-MM-dd`), matching the backend
 * summaries API. Use this for active/expired checks; keep {@link storedDayAsLocalDate} for pickers.
 */
export function storedDayAsUtcString(value: string | null | undefined): string | null {
    if (!value) return null;
    if (value.includes('T')) {
        const day = extractDayInTimezone(value, 'UTC');
        return day || null;
    }
    const day = value.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/** Today's calendar day in UTC, as `yyyy-MM-dd`. */
export function todayUtcString(date: Date = new Date()): string {
    return extractDayInTimezone(date.toISOString(), 'UTC');
}

/**
 * The timestamp as ISO 8601 with the zone's offset, e.g. "2024-01-17T00:30:00+01:00", and
 * "Z" while the zone is at UTC+0.
 */
export function toTimezoneISOString(
    timestamp: string | null | undefined,
    timezone = 'UTC'
): string {
    if (!timestamp) return '';
    const zoned = inZone(timestamp, timezone);
    return zoned ? format(zoned, "yyyy-MM-dd'T'HH:mm:ssXXX") : '';
}

/**
 * The timestamp's wall-clock time in `timeZone` in a date-fns pattern, or "".
 *
 * @example
 * formatInTimeZone('2024-01-16T23:30:00Z', 'yyyy_MM_dd', 'Europe/Oslo'); // "2024_01_17"
 */
export function formatInTimeZone(timestamp: DateInput, pattern: string, timeZone: string): string {
    const zoned = inZone(timestamp, timeZone);
    return zoned ? format(zoned, pattern) : '';
}

/**
 * The timestamp's wall-clock time in `timezone` as a worded date-time to the second (see
 * `formatDateTimeWorded`), in `language` (the page language by default): image times and a
 * bioscope's last alive, read in the site's zone. A caller that keeps the text, such as a
 * memoised image list, passes the language so the text follows a language switch.
 *
 * @example
 * formatDateTimeInTimezone('2026-01-19T22:43:23Z', 'Europe/Oslo'); // "Jan 19, 2026 23:43:23"
 */
export function formatDateTimeInTimezone(
    timestamp: string | null | undefined,
    timezone = 'UTC',
    language: string = appLanguage()
): string {
    if (!timestamp) return '';
    const zoned = inZone(timestamp, timezone);
    if (!zoned) return '';
    return format(zoned, dateTimeWordedPattern('second', language), {
        locale: dateLocale(language),
    });
}
